import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, Mail, MapPin, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AIRPORTS,
  SERVICES,
  INDUSTRIES,
  PROCESS,
  WHY,
  CONTACT_INFO,
} from "@/data/sections";
import { useWorldStore } from "@/components/world/store";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SectionPanel() {
  const openId = useWorldStore((s) => s.openId);
  const closePanel = useWorldStore((s) => s.closePanel);
  const resetInput = useWorldStore((s) => s.resetInput);
  const airport = AIRPORTS.find((a) => a.id === openId);

  const handleClose = () => {
    resetInput();
    closePanel();
  };

  return (
    <AnimatePresence>
      {airport && (
        <motion.div
          key="panel-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-40 bg-[rgba(3,2,5,0.75)] backdrop-blur-md flex items-end sm:items-center justify-center"
          onClick={handleClose}
          data-testid="world-panel-overlay"
        >
          <motion.div
            key="panel-card"
            initial={{ opacity: 0, y: 60, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 60, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative w-full sm:max-w-3xl lg:max-w-4xl max-h-[92vh] overflow-hidden rounded-t-2xl sm:rounded-2xl border border-[rgba(244,234,222,0.14)] bg-[rgba(11,10,12,0.96)] shadow-[0_-30px_120px_rgba(0,0,0,0.6)]"
            onClick={(e) => e.stopPropagation()}
            data-testid={`world-panel-${airport.id}`}
          >
            {/* Header */}
            <div
              className="relative px-6 sm:px-8 py-5 border-b border-[rgba(244,234,222,0.10)] flex items-start justify-between gap-4"
              style={{
                background: `linear-gradient(90deg, ${airport.color}22, transparent 70%)`,
              }}
            >
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: airport.color }}>
                  Airport {airport.code} · LANDED
                </div>
                <h2 className="mt-1 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#F4EADE]">
                  {airport.label}
                </h2>
                <p className="mt-2 text-sm text-[#EFE3D6]/75 max-w-2xl">{airport.intro}</p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                data-testid="world-panel-close-button"
                className="h-10 w-10 rounded-md border border-[rgba(244,234,222,0.14)] text-[#F4EADE] hover:border-[#E94B4B]/60 hover:text-[#E94B4B] transition-colors flex items-center justify-center"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto max-h-[calc(92vh-120px)] px-6 sm:px-8 py-6">
              {airport.id === "services" && <ServicesBody />}
              {airport.id === "industries" && <IndustriesBody />}
              {airport.id === "process" && <ProcessBody />}
              {airport.id === "why" && <WhyBody />}
              {airport.id === "contact" && <ContactBody onSubmitted={handleClose} />}
            </div>

            {/* Footer hint */}
            <div className="absolute bottom-3 right-4 font-mono text-[9px] uppercase tracking-[0.24em] text-[#B9AFA6] pointer-events-none">
              press <span className="text-[#F4EADE]">esc</span> or click outside to depart
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ------------------ Section bodies ------------------ */

function ServicesBody() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {SERVICES.map((s) => (
        <div key={s.title} className="rounded-xl border border-[rgba(244,234,222,0.10)] bg-[rgba(17,16,21,0.7)] p-5">
          <h3 className="font-display text-lg font-semibold text-[#F4EADE]">{s.title}</h3>
          <p className="mt-2 text-sm text-[#EFE3D6]/75 leading-relaxed">{s.body}</p>
        </div>
      ))}
    </div>
  );
}

function IndustriesBody() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {INDUSTRIES.map((it) => (
        <div key={it} className="rounded-xl border border-[rgba(244,234,222,0.10)] bg-[rgba(17,16,21,0.7)] p-4 text-center">
          <div className="h-1 w-6 bg-[#E94B4B] mx-auto mb-3" />
          <div className="font-display text-sm sm:text-base font-semibold text-[#F4EADE]">{it}</div>
        </div>
      ))}
    </div>
  );
}

function ProcessBody() {
  return (
    <ol className="space-y-4">
      {PROCESS.map((p) => (
        <li key={p.step} className="relative rounded-xl border border-[rgba(244,234,222,0.10)] bg-[rgba(17,16,21,0.7)] p-5 pl-16">
          <div className="absolute left-4 top-4 h-10 w-10 rounded-lg bg-[#6B1F47]/30 ring-1 ring-[#6B1F47]/60 text-[#F4EADE] flex items-center justify-center font-mono text-xs tracking-widest">
            {p.step}
          </div>
          <h3 className="font-display text-lg font-semibold text-[#F4EADE]">{p.label}</h3>
          <p className="mt-1 text-sm text-[#EFE3D6]/75 leading-relaxed">{p.body}</p>
        </li>
      ))}
    </ol>
  );
}

function WhyBody() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {WHY.map((w) => (
        <div key={w.title} className="rounded-xl border border-[rgba(244,234,222,0.10)] bg-[rgba(17,16,21,0.7)] p-5">
          <h3 className="font-display text-lg font-semibold text-[#F4EADE]">{w.title}</h3>
          <p className="mt-2 text-sm text-[#EFE3D6]/75 leading-relaxed">{w.body}</p>
        </div>
      ))}
    </div>
  );
}

const initialContact = {
  name: "",
  email: "",
  company: "",
  phone: "",
  country: "",
  message: "",
  website: "",
};

function ContactBody({ onSubmitted }) {
  const [form, setForm] = useState(initialContact);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const validate = (v) => {
    const e = {};
    if (!v.name || v.name.trim().length < 2) e.name = "Enter your full name.";
    if (!v.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = "Valid email required.";
    if (!v.company || v.company.trim().length < 1) e.company = "Company is required.";
    if (!v.message || v.message.trim().length < 10) e.message = "Please write 10+ characters.";
    return e;
  };

  const onChange = (k) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form, { timeout: 15000 });
      if (res.status >= 200 && res.status < 300) {
        setDone(true);
        setForm(initialContact);
        toast.success("Message sent. Our team will be in touch shortly.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="py-6 flex flex-col items-start gap-4" data-testid="world-contact-success">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#2FBF71]/15 ring-1 ring-[#2FBF71]/40 text-[#2FBF71]">
          <CheckCircle2 className="h-6 w-6" />
        </div>
        <h3 className="font-display text-2xl text-[#F4EADE]">Transmission received.</h3>
        <p className="text-sm text-[#EFE3D6]/80">Thanks for reaching out. Our team will respond within one business day.</p>
        <Button
          onClick={() => onSubmitted && onSubmitted()}
          className="mt-2 bg-[#6B1F47] hover:bg-[#7A1F4A] text-[#F4EADE]"
        >
          Return to airspace
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-[#6B1F47]/25 ring-1 ring-[#6B1F47]/40">
            <Mail className="h-4 w-4 text-[#F4EADE]" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">Email</div>
            <div className="font-display text-sm text-[#F4EADE]">{CONTACT_INFO.email}</div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-[#6B1F47]/25 ring-1 ring-[#6B1F47]/40">
            <MapPin className="h-4 w-4 text-[#F4EADE]" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">HQ</div>
            <div className="font-display text-sm text-[#F4EADE]">{CONTACT_INFO.address1}</div>
            <div className="font-display text-sm text-[#EFE3D6]/80">{CONTACT_INFO.address2}</div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-[#6B1F47]/25 ring-1 ring-[#6B1F47]/40">
            <Globe2 className="h-4 w-4 text-[#F4EADE]" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">Coverage</div>
            <div className="font-display text-sm text-[#F4EADE]">Worldwide — EMEA, AMER, APAC</div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="lg:col-span-3 space-y-4" data-testid="world-contact-form" noValidate>
        <div className="hidden" aria-hidden="true">
          <Input tabIndex={-1} autoComplete="off" value={form.website} onChange={onChange("website")} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldInput label="Full name" required v={form.name} err={errors.name} onChange={onChange("name")} testid="world-contact-name-input" placeholder="Jane Doe" />
          <FieldInput label="Work email" required type="email" v={form.email} err={errors.email} onChange={onChange("email")} testid="world-contact-email-input" placeholder="jane@company.com" />
          <FieldInput label="Company" required v={form.company} err={errors.company} onChange={onChange("company")} testid="world-contact-company-input" placeholder="Acme Global" />
          <FieldInput label="Country" v={form.country} onChange={onChange("country")} testid="world-contact-country-input" placeholder="e.g. Germany" />
          <FieldInput label="Phone" v={form.phone} onChange={onChange("phone")} testid="world-contact-phone-input" placeholder="+1 555 123 4567" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#EFE3D6]">Project summary <span className="text-[#E94B4B]">*</span></Label>
          <Textarea
            data-testid="world-contact-message-textarea"
            value={form.message}
            onChange={onChange("message")}
            rows={4}
            placeholder="Tell us about your platform, integrations, partners, timelines and what success looks like."
            className="bg-[#0B0A0C] border-[rgba(244,234,222,0.18)] text-[#F4EADE] placeholder:text-[#B9AFA6]/60 focus-visible:ring-[#E94B4B]"
          />
          {errors.message && <p className="text-xs text-[#E94B4B]">{errors.message}</p>}
        </div>
        <div className="flex items-center justify-end pt-1">
          <Button
            type="submit"
            disabled={loading}
            data-testid="world-contact-submit-button"
            className="h-11 px-5 bg-[#6B1F47] hover:bg-[#7A1F4A] text-[#F4EADE] disabled:opacity-60"
          >
            {loading ? "Sending…" : (<><Send className="mr-2 h-4 w-4" /> Send message</>)}
          </Button>
        </div>
      </form>
    </div>
  );
}

function FieldInput({ label, required, type = "text", v, err, onChange, testid, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#EFE3D6]">
        {label} {required && <span className="text-[#E94B4B]">*</span>}
      </Label>
      <Input
        type={type}
        value={v}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        aria-invalid={!!err}
        className="bg-[#0B0A0C] border-[rgba(244,234,222,0.18)] text-[#F4EADE] placeholder:text-[#B9AFA6]/60 focus-visible:ring-[#E94B4B]"
      />
      {err && <p className="text-xs text-[#E94B4B]">{err}</p>}
    </div>
  );
}
